import { zodResolver } from "@hookform/resolvers/zod";
import { SignInIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation } from "react-router";
import { z } from "zod";

import { type SignInChallenge, useSession } from "../auth/session";
import { signInFailureMessage } from "../auth/sign-in-copy";
import { ApiProblem, type Schemas } from "../lib/api/client";
import { createPasskey, getPasskey, passkeyFailureMessage, passkeysSupported } from "../lib/webauthn";
import { StatusBanner } from "../components/feedback/StatusBanner";
import { Button } from "../components/ui/Button";
import { TextField } from "../components/ui/TextField";

const credentialsSchema = z.object({
  email: z.string().trim().min(1, "Enter your email address."),
  // Length and content rules are the server's; a sign-in form only needs
  // something to send, so it gives nothing away about the policy.
  password: z.string().min(1, "Enter your password."),
});

const codeSchema = z.object({
  code: z.string().trim().regex(/^[0-9]{6}$/, "Enter the six digits from your authenticator app."),
});

type Credentials = z.infer<typeof credentialsSchema>;
type Code = z.infer<typeof codeSchema>;

/**
 * Sign-in with password, then the authenticator code (P1-017, P1-019).
 *
 * Nothing typed here is stored: react-hook-form keeps it in memory and the
 * request carries it to the API over the page's own origin. The layout follows
 * the manager portal's sign-in in the Claude Design reference (ADR 0027): an
 * explanation beside the form, stacked on a phone.
 */
export function SignInScreen() {
  const { completePasskeyEnrolment, completePasskeySignIn, completeSignIn, session, startSignIn } = useSession();
  const location = useLocation();
  const [step, setStep] = useState<"credentials" | "code" | "passkey" | "enrol">("credentials");
  const [failure, setFailure] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<SignInChallenge | null>(null);
  const [enrolOptions, setEnrolOptions] = useState<{ options: Schemas["PasskeyCreationOptions"]; expiresAt: string } | null>(null);
  const [passkeyName, setPasskeyName] = useState("");
  const [busy, setBusy] = useState(false);

  const credentials = useForm<Credentials>({ resolver: zodResolver(credentialsSchema) });
  const code = useForm<Code>({ resolver: zodResolver(codeSchema) });

  if (session.kind === "signed-in") {
    const from = (location.state as { from?: unknown } | null)?.from;
    return <Navigate replace to={typeof from === "string" && from.startsWith("/") ? from : "/"} />;
  }

  const submitCredentials = credentials.handleSubmit(async ({ email, password }) => {
    setFailure(null);
    try {
      const next = await startSignIn(email, password);
      credentials.reset({ email, password: "" });
      setChallenge(next);
      setStep(next.state === "PASSKEY_REQUIRED" ? "passkey" : "code");
    } catch (error) {
      setFailure(signInFailureMessage(error, "credentials"));
    }
  });

  const submitCode = code.handleSubmit(async ({ code: value }) => {
    setFailure(null);
    try {
      const enrol = await completeSignIn(value);
      if (enrol) {
        // A Superadmin with no passkey creates one before the session starts.
        setEnrolOptions(enrol);
        setStep("enrol");
      }
    } catch (error) {
      code.reset({ code: "" });
      setFailure(signInFailureMessage(error, "code"));
    }
  });

  // One passkey ceremony, then the step's own request. A cancelled prompt
  // stays on the step so the person can try again.
  const withPasskey = async <T,>(
    ceremony: () => Promise<{ ok: true; credential: T } | { ok: false; reason: Parameters<typeof passkeyFailureMessage>[0] }>,
    finish: (credential: T) => Promise<void>,
  ) => {
    setFailure(null);
    setBusy(true);
    try {
      const result = await ceremony();
      if (!result.ok) {
        setFailure(passkeyFailureMessage(result.reason));
        return;
      }
      await finish(result.credential);
    } catch (error) {
      setFailure(signInFailureMessage(error, "passkey"));
    } finally {
      setBusy(false);
    }
  };

  // A timed-out transaction cannot finish; for enrolment the prompt would
  // leave a passkey on the device that the server never learns of.
  const timedOut = (expiresAt: string) => {
    if (Date.parse(expiresAt) > Date.now()) return false;
    setFailure("This sign-in has timed out. Start again.");
    return true;
  };

  const usePasskey = () => {
    const options = challenge?.passkeyOptions;
    if (!options || timedOut(challenge.expiresAt)) return;
    void withPasskey(() => getPasskey(options), completePasskeySignIn);
  };

  const createFirstPasskey = () => {
    if (!enrolOptions || timedOut(enrolOptions.expiresAt)) return;
    void withPasskey(
      () => createPasskey(enrolOptions.options),
      (credential) => completePasskeyEnrolment(credential, passkeyName.trim() || undefined),
    );
  };

  const startAgain = () => {
    setFailure(null);
    setChallenge(null);
    setEnrolOptions(null);
    setPasskeyName("");
    code.reset({ code: "" });
    setStep("credentials");
  };

  return (
    <div className="grid max-w-5xl gap-10 lg:grid-cols-[1fr_minmax(0,28rem)] lg:gap-16">
      <div className="max-w-xl lg:pt-6">
        <span aria-hidden="true" className="grid size-14 place-items-center rounded-full bg-accent-soft">
          <SignInIcon className="size-7 text-accent-strong" weight="duotone" />
        </span>
        <h1 className="mt-6 text-4xl font-extrabold tracking-tight">Sign in</h1>
        <p className="mt-3 leading-7 text-text-muted">
          Use the email and password for your BurgePOS account, then the six-digit code from your
          authenticator app, or your passkey.
        </p>
        <p className="mt-4 leading-7 text-text-muted">
          Cashiers do not use this workspace. Selling, taking payment and running a shift happen on the
          shop's Windows till, with its own PIN.
        </p>
      </div>

      <section aria-labelledby="sign-in-step" className="rounded-panel bg-surface-raised p-6 shadow-panel sm:p-8">
        {session.kind === "idle-locked" ? (
          <UnlockForm />
        ) : (
          <>
          {failure ? (
            <p className="mb-6 rounded-control border border-danger-border bg-danger-soft px-4 py-3 text-sm font-medium text-danger-strong" role="alert">
              {failure}
            </p>
          ) : null}

          {/*
            Each step is keyed, so the code field is a new input: it never keeps
            the email field's value or listeners, and its autoFocus runs.
          */}
          {step === "credentials" ? (
            <form key="credentials" noValidate onSubmit={(event) => void submitCredentials(event)}>
              <h2 className="text-xl font-bold tracking-tight" id="sign-in-step">
                Your account
              </h2>
              <TextField
                autoComplete="username"
                className="mt-6"
                error={credentials.formState.errors.email?.message}
                label="Email address"
                type="email"
                {...credentials.register("email")}
              />
              <TextField
                autoComplete="current-password"
                className="mt-5"
                error={credentials.formState.errors.password?.message}
                label="Password"
                type="password"
                {...credentials.register("password")}
              />
              <Button className="mt-7 w-full" disabled={credentials.formState.isSubmitting} type="submit">
                {credentials.formState.isSubmitting ? "Checking…" : "Continue"}
              </Button>
              <Link
                className="mt-3 flex min-h-12 items-center justify-center rounded-full text-sm font-bold text-accent-strong underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                to="/password-reset"
              >
                Forgot your password?
              </Link>
            </form>
          ) : step === "passkey" || step === "enrol" ? (
            <div key={step}>
              <h2 className="text-xl font-bold tracking-tight" id="sign-in-step">
                {step === "enrol" ? "Create your passkey" : "Use your passkey"}
              </h2>
              <p className="mt-3 leading-7 text-text-muted">
                {step === "enrol"
                  ? "BurgePOS staff accounts need a passkey. Your device will ask for your fingerprint, face, PIN or security key. We recommend adding a second passkey later, such as your phone."
                  : "Your device will ask for your fingerprint, face, PIN or security key."}
              </p>
              {passkeysSupported() ? null : (
                <p className="mt-4 text-sm font-medium text-danger-strong">{passkeyFailureMessage("unsupported")}</p>
              )}
              {step === "enrol" ? (
                <TextField
                  autoComplete="off"
                  className="mt-5"
                  hint="Optional. A name to tell your passkeys apart, such as Shop laptop."
                  label="Passkey name"
                  maxLength={64}
                  onChange={(event) => setPasskeyName(event.target.value)}
                  value={passkeyName}
                />
              ) : null}
              <Button
                autoFocus
                className="mt-7 w-full"
                disabled={busy || !passkeysSupported()}
                onClick={step === "enrol" ? createFirstPasskey : usePasskey}
                type="button"
              >
                {busy ? "Waiting for your device…" : step === "enrol" ? "Create passkey" : "Use your passkey"}
              </Button>
              {step === "passkey" && challenge?.state === "TOTP_REQUIRED" ? (
                <Button className="mt-3 w-full" onClick={() => setStep("code")} type="button" variant="quiet">
                  Use the authenticator code instead
                </Button>
              ) : null}
              <Button className="mt-3 w-full" onClick={startAgain} type="button" variant="quiet">
                Start again
              </Button>
            </div>
          ) : (
            <form key="code" noValidate onSubmit={(event) => void submitCode(event)}>
              <h2 className="text-xl font-bold tracking-tight" id="sign-in-step">
                Authenticator code
              </h2>
              <TextField
                autoComplete="one-time-code"
                // Focus moves here when the step changes, for keyboard and
                // screen-reader users alike.
                autoFocus
                className="mt-6"
                error={code.formState.errors.code?.message}
                hint="Open your authenticator app and enter the code for BurgePOS. It changes every 30 seconds."
                inputMode="numeric"
                label="Six-digit code"
                maxLength={6}
                {...code.register("code")}
              />
              <Button className="mt-7 w-full" disabled={code.formState.isSubmitting} type="submit">
                {code.formState.isSubmitting ? "Signing in…" : "Sign in"}
              </Button>
              {challenge?.passkeyOptions ? (
                <Button className="mt-3 w-full" onClick={() => setStep("passkey")} type="button" variant="secondary">
                  Use a passkey instead
                </Button>
              ) : null}
              <Button className="mt-3 w-full" onClick={startAgain} variant="quiet">
                Start again
              </Button>
              <Link
                className="mt-1 flex min-h-12 items-center justify-center rounded-full text-sm font-bold text-accent-strong underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                to="/replace-authenticator"
              >
                Lost your authenticator?
              </Link>
              <Link
                className="mt-1 flex min-h-12 items-center justify-center rounded-full text-center text-sm font-bold text-accent-strong underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                to="/assisted-recovery"
              >
                Lost your authenticator app and recovery codes?
              </Link>
            </form>
          )}
          </>
        )}
      </section>
    </div>
  );
}

const unlockSchema = z.object({ password: z.string().min(1, "Enter your password.") });

/**
 * Unlock after time away (P1-024): the password alone brings the session
 * back, so nobody needs their phone for it. The six-digit code is still asked
 * for sensitive actions. Someone else at this computer signs in afresh.
 */
function UnlockForm() {
  const { refresh, signOut, unlock } = useSession();
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ password: string }>({ resolver: zodResolver(unlockSchema) });

  const submit = form.handleSubmit(async ({ password }) => {
    setFailure(null);
    try {
      await unlock(password);
    } catch (error) {
      form.reset({ password: "" });
      setFailure(
        error instanceof ApiProblem && error.status === 401
          ? "That password was not accepted. Try again, or sign in as someone else."
          : signInFailureMessage(error, "credentials"),
      );
      // The same refusal comes back once the session has passed its 12 hours:
      // asking again shows the full sign-in instead of refusing forever.
      if (error instanceof ApiProblem && error.status === 401) await refresh().catch(() => undefined);
    }
  });

  return (
    <form key="unlock" noValidate onSubmit={(event) => void submit(event)}>
      <StatusBanner
        className="mb-6"
        description="You were away for a while, so BurgePOS locked this page. Enter your password to carry on where you left off."
        title="Welcome back"
      />
      {failure ? (
        <p className="mb-6 rounded-control border border-danger-border bg-danger-soft px-4 py-3 text-sm font-medium text-danger-strong" role="alert">
          {failure}
        </p>
      ) : null}
      <h2 className="text-xl font-bold tracking-tight" id="sign-in-step">
        Unlock
      </h2>
      <TextField
        autoComplete="current-password"
        autoFocus
        className="mt-6"
        error={form.formState.errors.password?.message}
        label="Password"
        type="password"
        {...form.register("password")}
      />
      <Button className="mt-7 w-full" disabled={form.formState.isSubmitting} type="submit">
        {form.formState.isSubmitting ? "Unlocking…" : "Unlock"}
      </Button>
      <Button className="mt-3 w-full" onClick={() => void signOut().catch(() => undefined)} variant="quiet">
        Sign in as someone else
      </Button>
    </form>
  );
}
